<template>
  <!-- CUSTOMER INFO -->
  <div class="card border-2 bg-white rounded-3 shadow-sm mb-3">
    <div class="p-4">
      <div class="text-center mb-3">
        <div class="d-flex align-items-center justify-content-center mb-3">
          <div class="icon-line flex-grow-1"></div>
          <div
            class="customer-initial-icon bg-green d-flex align-items-center justify-content-center mx-3"
          >
            <span class="fw-bold text-dark">{{ getInitial }}</span>
          </div>
          <div class="icon-line flex-grow-1"></div>
        </div>
        <h5 class="fw-bold mb-1">{{ first_name }} {{ last_name }}</h5>
        <p class="text-muted text-sm mb-0">{{ email }}</p>
      </div>

      <div
        class="customer-info-row d-flex justify-content-center align-items-start mb-3 text-xs"
      >
        <div class="text-center px-3">
          <span class="fw-bold text-muted d-block mb-1">Phone</span>
          <span class="fw-normal d-block info-value">{{ phone_number }}</span>
        </div>
        <div class="divider-line">|</div>
        <div class="text-center px-3">
          <span class="fw-bold text-muted d-block mb-1">Date Joined</span>
          <span class="fw-normal d-block info-value">{{
            formatDate(date_joined)
          }}</span>
        </div>
        <div v-if="reference" class="divider-line">|</div>
        <div v-if="reference" class="text-center px-3">
          <span class="fw-bold text-muted d-block mb-1">Reference</span>
          <span class="fw-normal d-block info-value"
            >{{ reference }} reference</span
          >
        </div>
        <div class="divider-line">|</div>
        <div class="text-center px-3">
          <span class="fw-bold text-muted d-block mb-1">Current Status</span>
          <div class="mt-2">
            <span
              class="fw-bold text-xs px-2 py-1 rounded-pill bg-info text-white d-inline-block"
            >
              <i class="fa fa-check-circle me-1" aria-hidden="true"></i>
              {{ currentApplicationStatus || "Loading..." }}
            </span>
          </div>
        </div>
      </div>

      <!-- Change Status Button -->
      <div
        class="text-center mb-3"
        v-if="
          workflowStatuses.length > 0 &&
          currentApplicationStatus != 'Incomplete'
        "
      >
        <button
          class="btn bg-green text-dark text-xs rounded-3 btn-sm shadow-sm"
          @click="openStatusModal"
        >
          <i class="fa fa-edit me-1"></i> Change Status
        </button>
      </div>

      <!-- Send to Zoho Button -->
      <div
        class="text-center"
        v-if="showZohoExport"
      >
        <button
          class="btn btn-bg-green text-xs rounded-small shadow-sm"
          @click="handleZohoSendClick"
          :disabled="is_sending_to_zoho"
        >
          <i v-if="is_sending_to_zoho" class="bi bi-arrow-repeat spin me-1"></i>
          <i v-else-if="all_zoho_reports_sent" class="bi bi-arrow-clockwise me-1"></i>
          <i v-else class="bi bi-send me-1"></i>
          {{ is_sending_to_zoho ? 'Sending to Zoho...' : (all_zoho_reports_sent ? 'Re-send to Zoho' : 'Send to Zoho') }}
        </button>
      </div>
    </div>
  </div>

  <!-- RISK SUMMARY, ASSET INFO, AND ON BEHALF INFO -->
  <div class="row g-3 mb-3">
    <!-- RISK SUMMARY -->
    <div v-if="hasRiskSummary" :class="getRiskSummaryColClass">
      <div class="card border-2 bg-white rounded-3 shadow-sm h-100">
        <div class="p-3">
          <h6 class="text-uppercase text-sm fw-bold border-bottom pb-2 mb-3">
            Risk Summary
          </h6>
          <RiskSummary
            :services="filteredServices"
            :loading="riskDataMap.risk_loading"
            @service-clicked="handleRiskServiceClick"
          />
        </div>
      </div>
    </div>

    <!-- ASSET INFO -->
    <div v-if="asset_info" :class="getAssetInfoColClass">
      <div class="card border-2 bg-white rounded-3 shadow-sm h-100">
        <div class="p-3">
          <h6 class="text-uppercase text-sm fw-bold border-bottom pb-2 mb-3">
            Asset Info
          </h6>

          <div class="d-flex align-items-start gap-3">
            <!-- Asset Image Thumbnail -->
            <div
              v-if="asset_info?.asset_image"
              class="asset-thumbnail-wrapper flex-shrink-0"
            >
              <img
                :src="asset_info.asset_image"
                class="asset-thumbnail"
                :alt="asset_info.asset_name"
              />
            </div>

            <!-- Asset Details -->
            <div class="flex-grow-1">
              <div class="mb-2 text-xs">
                <span class="fw-bold text-muted d-block mb-1">Asset Name</span>
                <span class="fw-normal">{{ asset_info.asset_name }}</span>
              </div>

              <div v-if="asset_info?.asset_description" class="text-xs">
                <span class="fw-bold text-muted d-block mb-1">Description</span>
                <span class="fw-normal">{{
                  asset_info.asset_description
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ON BEHALF INFO -->
    <div v-if="on_behalf_info" :class="getOnBehalfInfoColClass">
      <div class="card border-2 bg-white rounded-3 shadow-sm h-100">
        <div class="p-3">
          <h6 class="text-uppercase text-sm fw-bold border-bottom pb-2 mb-3">
            On Behalf Of
          </h6>
          <div class="mb-2 text-xs">
            <span class="fw-bold text-muted d-block mb-1">Full Name</span>
            <span class="fw-normal">{{ on_behalf_info.full_name }}</span>
          </div>
          <div class="mb-2 text-xs">
            <span class="fw-bold text-muted d-block mb-1">Workflow</span>
            <span class="fw-normal">{{ workflow_name || "N/A" }}</span>
          </div>
          <div v-if="on_behalf_info.description" class="text-xs">
            <span class="fw-bold text-muted d-block mb-1">Description</span>
            <span class="fw-normal">{{ on_behalf_info.description }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="showZohoExport"
    class="collapsible-section"
  >
    <div @click="toggleExpanded('zoho')" class="collapsible-header">
      <p
        class="text-uppercase text-xs text-blue fw-bold mt-4 d-flex align-items-center"
      >
        <i
          :class="
            expandedSections && expandedSections.zoho
              ? 'fa fa-chevron-down'
              : 'fa fa-chevron-right'
          "
          class="me-2 collapsible-icon"
        ></i>
        Send to Zoho
      </p>
    </div>
    <div
      v-if="expandedSections && expandedSections.zoho"
      class="collapsible-content expanded"
    >
      <div class="underline rounded-small mt-1"></div>
      <ZohoApplication
        ref="zohoApplication"
        :workflow_id="workflow_id"
        :get_user_id="get_user_id"
        :id_number="id_number"
        :customer_application_id="customer_application"
        :credit_bureau_services="credit_bureau_services"
        :financial_services="financial_services"
        :aml-target-module="amlTargetModule"
        @services-loaded="handleServicesLoaded"
        @sending-status-changed="handleSendingStatusChanged"
        @aml-target-module-selected="handleAmlTargetModuleSelected"
        @zoho-document-sent="handleZohoDocumentSent"
      />
    </div>
  </div>

  <div
    v-if="['mobiafrica', 'verify', 'swift'].includes(client)"
    class="collapsible-section"
  >
  <hr class="border-0" />
    <div @click="toggleExpanded('seriti')" class="collapsible-header">
      <p
        class="text-uppercase text-xs text-blue fw-bold mt-4 d-flex align-items-center"
      >
        <i
          :class="
            expandedSections && expandedSections.seriti
              ? 'fa fa-chevron-down'
              : 'fa fa-chevron-right'
          "
          class="me-2 collapsible-icon"
        ></i>
        Seriti Policy
      </p>
    </div>
    <transition name="accordion-fade" mode="out-in">
      <div
        v-if="seritiMounted && expandedSections && expandedSections.seriti"
        key="seriti-content"
        class="collapsible-content expanded"
      >
        <div class="underline rounded-small mt-1"></div>
        <SeritiPolicy
          ref="seritiPolicyRef"
          :key="`seriti-${workflow_id}-${customer_application}`"
          :workflow_id="workflow_id"
          :get_user_id="get_user_id"
          :id_number="id_number"
          :credit_bureau_services="credit_bureau_services"
          :financial_services="financial_services"
        />
      </div>
    </transition>
  </div>
  <div
    v-if="
      ['suzuki', 'verify', 'mobiafrica', 'autocity', 'swift'].includes(client)
    "
    class="collapsible-section"
  >
    <hr class="border-0" />
    <div @click="toggleExpanded('signio')" class="collapsible-header">
      <p
        class="text-uppercase text-xs text-blue fw-bold mt-4 d-flex align-items-center"
      >
        <i
          :class="
            expandedSections && expandedSections.signio
              ? 'fa fa-chevron-down'
              : 'fa fa-chevron-right'
          "
          class="me-2 collapsible-icon"
        ></i>
        Signio Policy
      </p>
    </div>
    <transition name="accordion-fade" mode="out-in">
      <div
        v-if="signioMounted && expandedSections && expandedSections.signio"
        key="signio-content"
        class="collapsible-content expanded"
      >
        <div class="underline rounded-small mt-1"></div>
        <SygnioApplication
          :key="`signio-${workflow_id}-${customer_application}`"
          :workflow_id="workflow_id"
          :customer_application="customer_application"
        />
      </div>
    </transition>
  </div>
  <div class="collapsible-section">
    <hr class="border-0" />
    <div @click="toggleExpanded('internalForms')" class="collapsible-header">
      <p
        class="text-uppercase text-xs text-blue fw-bold mt-4 d-flex align-items-center"
      >
        <i
          :class="
            expandedSections && expandedSections.internalForms
              ? 'fa fa-chevron-down'
              : 'fa fa-chevron-right'
          "
          class="me-2 collapsible-icon"
        ></i>
        Internal Forms
      </p>
    </div>
    <transition name="accordion-fade" mode="out-in">
      <div
        v-if="internalFormsMounted && expandedSections && expandedSections.internalForms"
        key="internal-forms-content"
        class="collapsible-content expanded"
      >
        <div class="underline rounded-small mt-1"></div>
        <InternalForm
          :key="`internal-${get_user_id}-${customer_application}`"
          :get_user_id="get_user_id"
          :customer_application="customer_application"
        />
      </div>
    </transition>
  </div>

  <Teleport to="body">
    <transition name="modal-fade">
      <div v-if="showStatusModal" class="customer-profile-modal-overlay" @click.self="closeStatusModal">
        <div class="customer-profile-modal-dialog">
          <div class="customer-profile-modal-content">
            <div class="customer-profile-modal-header">
              <h5 class="modal-title fs-6 text-dark fw-bold">Change Application Status</h5>
              <button type="button" class="btn-close" @click="closeStatusModal"></button>
            </div>
            <div class="customer-profile-modal-body">
              <div class="mb-3">
                <label for="statusSelect" class="form-label text-xs fw-semibold text-muted">Select New Status</label>
                <select class="form-select" id="statusSelect" v-model="selectedStatus">
                  <option value="" hidden >-- Select Status --</option>
                  <option v-for="status in workflowStatuses" :key="status.id" :value="status.id">
                    {{ status.status }}
                  </option>
                </select>
              </div>
            </div>
            <div class="customer-profile-modal-footer">
              <button type="button" class="btn btn-bg-secondary rounded-3 text-xs btn-sm" @click="closeStatusModal">Cancel</button>
              <button type="button" class="btn btn-bg-green rounded-3 text-xs btn-sm" @click="changeStatus" :disabled="!selectedStatus">Change Status</button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>

  <Teleport to="body">
    <transition name="aml-target-modal-fade">
      <div v-if="showAmlDestinationModal" class="aml-target-modal-overlay" @click.self="closeAmlDestinationModal">
        <div class="aml-target-modal-card">
          <div class="aml-target-modal-header">
            <div>
              <p class="aml-target-modal-kicker">AML destination</p>
              <h5 class="aml-target-modal-title mb-0">Select where to send the AML report</h5>
            </div>
            <button type="button" class="btn-close" aria-label="Close" @click="closeAmlDestinationModal"></button>
          </div>
          <div class="aml-target-modal-body">
            <p class="text-sm text-muted mb-3">
              Choose the Zoho module before sending the Anti Money Laundering document.
            </p>
            <label for="amlDestinationSelect" class="form-label text-xs text-muted text-uppercase fw-semibold">
              Zoho destination
            </label>
            <select class="form-select" id="amlDestinationSelect" v-model="pendingAmlTargetModule">
              <option value="Contacts">Contacts</option>
              <option value="Leads">Leads</option>
            </select>
          </div>
          <div class="aml-target-modal-footer">
            <button type="button" class="btn btn-bg-secondary text-xs rounded-small shadow-sm" @click="closeAmlDestinationModal">Cancel</button>
            <button type="button" class="btn btn-bg-green text-xs rounded-small shadow-sm" @click="confirmAmlDestinationSelection">Continue</button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>
<script>
import { API_BASE_URL } from "@/config";
import { defineAsyncComponent } from "vue";
import { format_date } from "@/utils/date_formatter";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner.vue";

export default {
  props: [
    "first_name",
    "last_name",
    "email",
    "reference",
    "phone_number",
    "date_joined",
    "workflow_id",
    "id_number",
    "customer_application",
    "get_user_id",
    "credit_bureau_services",
    "financial_services",
    "showRiskSummary",
  ],
  components: {
    SygnioApplication: defineAsyncComponent({
      loader: () =>
        import(
          "@/layouts/client/customer_data/sygnio_component/SygnioApplication.vue"
        ),
      loadingComponent: LoadingSpinner,
      delay: 200,
    }),
    InternalForm: defineAsyncComponent({
      loader: () =>
        import(
          "@/layouts/client/customer_data/internal_forms/InternalForm.vue"
        ),
      loadingComponent: LoadingSpinner,
      delay: 200,
    }),
    SeritiPolicy: defineAsyncComponent({
      loader: () =>
        import(
          "@/layouts/client/customer_data/seriti_component/SeritiApplication.vue"
        ),
      loadingComponent: LoadingSpinner,
      delay: 200,
    }),
    ZohoApplication: defineAsyncComponent({
      loader: () =>
        import(
          "@/layouts/client/customer_data/zoho_component/ZohoApplication.vue"
        ),
      loadingComponent: LoadingSpinner,
      delay: 200,
    }),
    RiskSummary: defineAsyncComponent({
      loader: () => import("@/components/RiskMatrix/RiskSummary.vue"),
      loadingComponent: LoadingSpinner,
      delay: 200,
    }),
  },
  data: () => ({
    workflowStatuses: [],
    showStatusModal: false,
    selectedStatus: '',
    currentApplicationStatus: null,
    riskDataMap: { risk_loading: false, risk_data: [], risk_error: null },
    on_behalf_info: null,
    asset_info: null,
    workflow_name: "" || "N/A",
    client: "",
    expandedSections: {
      zoho: false,
      seriti: false,
      signio: false,
      internalForms: false,
    },
    seritiMounted: false,
    signioMounted: false,
    internalFormsMounted: false,
    componentReady: false,
    services_data: {
      idv_available: false,
      aml_available: false,
    },
    is_sending_to_zoho: false,
    showZohoExport: false,
    amlTargetModule: "Contacts",
    pendingAmlTargetModule: "Contacts",
    showAmlDestinationModal: false,
  }),
  computed: {
    getInitial() {
      return this.first_name ? this.first_name.charAt(0).toUpperCase() : "?";
    },
    filteredServices() {
      if (
        !this.riskDataMap.risk_data ||
        !Array.isArray(this.riskDataMap.risk_data)
      ) {
        return [];
      }
      return this.riskDataMap.risk_data.filter(
        (service) =>
          service.severity && service.severity.toLowerCase() !== "unknown"
      );
    },
    totalScore() {
      return this.filteredServices.reduce(
        (total, service) => total + (service.total_score || 0),
        0
      );
    },
    totalMaxScore() {
      return this.filteredServices.reduce(
        (total, service) => total + (service.max_possible_score || 0),
        0
      );
    },
    overallPercentage() {
      if (this.totalMaxScore === 0) return 0;
      return Math.round((this.totalScore / this.totalMaxScore) * 100);
    },
    overallPercentageClass() {
      return `severity-${this.overallSeverity}`;
    },
    hasRiskSummary() {
      return this.filteredServices.length > 0 || this.riskDataMap.risk_loading;
    },
    hasAllThreeSections() {
      return this.hasRiskSummary && this.asset_info && this.on_behalf_info;
    },
    getRiskSummaryColClass() {
      if (this.hasAllThreeSections) return "col-12";
      if (!this.asset_info && !this.on_behalf_info) return "col-12";
      return "col-lg-6";
    },
    getAssetInfoColClass() {
      if (this.hasAllThreeSections) return "col-lg-6";
      if (!this.hasRiskSummary && !this.on_behalf_info) return "col-12";
      return "col-lg-6";
    },
    getOnBehalfInfoColClass() {
      if (this.hasAllThreeSections) return "col-lg-6";
      if (!this.hasRiskSummary && !this.asset_info) return "col-12";
      return "col-lg-6";
    },
    has_zoho_reports() {
      if (!this.credit_bureau_services || !Array.isArray(this.credit_bureau_services)) {
        return false;
      }
      const has_idv = this.credit_bureau_services.some(
        (s) => s.service_name === "RSA ID Verification"
      );
      const has_aml = this.credit_bureau_services.some(
        (s) => s.service_name === "Anti Money Laundering"
      );
      return has_idv || has_aml;
    },
    all_zoho_reports_sent() {
      if (!this.credit_bureau_services || !Array.isArray(this.credit_bureau_services)) {
        return false;
      }
      
      const idv_service = this.credit_bureau_services.find(
        (s) => s.service_name === "RSA ID Verification"
      );
      const aml_service = this.credit_bureau_services.find(
        (s) => s.service_name === "Anti Money Laundering"
      );

      const has_any_report = idv_service || aml_service;
      if (!has_any_report) return false;

      const idv_sent = !idv_service || idv_service.zoho_sent_at;
      const aml_sent = !aml_service || aml_service.zoho_sent_at;
      
      return idv_sent && aml_sent;
    },
    hasAmlZohoReport() {
      if (!this.credit_bureau_services || !Array.isArray(this.credit_bureau_services)) {
        return false;
      }

      return this.credit_bureau_services.some(
        (service) => service.service_name === "Anti Money Laundering"
      );
    },
  },
  created() {
    const domain = window.location.hostname;
    if (domain.includes("verify")) {
      this.client = "verify";
    } else if (domain.includes("mobiafrica")) {
      this.client = "mobiafrica";
    } else if (domain.includes("suzuki")) {
      this.client = "suzuki";
    } else if (domain.includes("autocity")) {
      this.client = "autocity";
    } else if (domain.includes("yumbi")) {
      this.client = "yumbi";
    } else if (domain.includes("santam")) {
      this.client = "santam";
    } else if (domain.includes("altron")) {
      this.client = "altron";
    } else if (domain.includes("swift")) {
      this.client = "swift";
    } else if (domain.includes("webuycars")) {
      this.client = "webuycars";
    } else if (domain.includes("getsavvi")) {
      this.client = "getsavvi";
    }
  },
  async mounted() {
    this.$nextTick(() => {
      this.componentReady = true;
    });
    await this.fetchFeatureFlags();
  },
  methods: {
    async fetchFeatureFlags() {
      try {
        const response = await fetch(
          `${API_BASE_URL}/verify_api/system_management_api/feature-flags/`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        const flags = data.flags || [];
        this.showZohoExport = flags.some(
          (flag) => flag.name === "export_to_zoho" && flag.active
        );
      } catch {
        this.showZohoExport = false;
      }
    },
    formatDate(date) {
      return format_date(date);
    },
    // workflow status related methods
    fetchWorkflowStatuses() {
      const token = localStorage.getItem("token");
      const base_url = API_BASE_URL;
      const url = `${base_url}/verify_api/workflow_management_api/workflow_application_status/get_by_workflow/?workflow_id=${this.workflow_id}`;

      $.ajax({
        method: "GET",
        url: url,
        contentType: "application/json",
        headers: {
          Authorization: `Token ${token}`,
        },
        success: (response) => {
          if (response.status === "success") {
            this.workflowStatuses = response.data || [];
          } else {
            this.workflowStatuses = [];
          }
        },
        error: () => {
          this.workflowStatuses = [];
        },
      });
    },
    fetchCurrentApplicationStatus() {
      const token = localStorage.getItem("token");
      const base_url = API_BASE_URL;
      const url = `${base_url}/verify_api/customer_management_api/get_current_application_status_api/`;

      $.ajax({
        method: "POST",
        url: url,
        contentType: "application/json",
        headers: {
          Authorization: `Token ${token}`,
        },
        data: JSON.stringify({
          customer_application_id: this.customer_application,
        }),
        success: (response) => {
          if (response.status === "success") {
            this.currentApplicationStatus = response.data.current_status;
          } else {
            this.currentApplicationStatus = null;
          }
        },
        error: () => {
          this.currentApplicationStatus = null;
        },
      });
    },
    openStatusModal() {
      this.showStatusModal = true;
    },
    closeStatusModal() {
      this.showStatusModal = false;
      this.selectedStatus = '';
    },
    changeStatus() {
      if (!this.selectedStatus) return;

      const token = localStorage.getItem("token");
      const base_url = API_BASE_URL;
      const url = `${base_url}/verify_api/customer_management_api/assign_customer_application_status_api/`;

      const data = {
        workflow_id: this.workflow_id,
        status_id: this.selectedStatus,
        customer_application_id: this.customer_application,
      };

      $.ajax({
        method: "POST",
        url: url,
        contentType: "application/json",
        headers: {
          Authorization: `Token ${token}`,
        },
        data: JSON.stringify(data),
        success: (response) => {
          if (response.status === "success") {
            Swal.fire({
              icon: "success",
              text: "Status changed successfully",
            });
            this.closeStatusModal();
            // Refresh the current status and workflow statuses
            this.fetchCurrentApplicationStatus();
            this.fetchWorkflowStatuses();
          } else {
            Swal.fire({
              icon: "error",
              text: response.message || "Failed to change status",
            });
          }
        },
        error: () => {
          Swal.fire({
            icon: "error",
            text: "An error occurred while changing status",
          });
        },
      });
    },
    get_workflow_details() {
      const token = localStorage.getItem("token");
      const base_url = API_BASE_URL;

      if (!this.workflow_id || this.workflow_id === 0) {
        return;
      }

      const url = `${base_url}/verify_api/workflow_management_api/get_workflow_details/${this.workflow_id}/`;

      $.ajax({
        method: "GET",
        url: url,
        contentType: "application/json",
        headers: {
          Authorization: `Token ${token}`,
        },
        success: (response) => {
          if (response.status === "success" && response.data) {
            this.workflow_name = response.data.workflow_name || "N/A";
            this.workflow_description = response.data.description || "";
          }
        },
        error: () => {
          this.workflow_name = "N/A";
        },
      });
    },
    // on behalf info
    get_on_behalf_info() {
      const token = localStorage.getItem("token");
      const base_url = API_BASE_URL;

      const url = `${base_url}/verify_api/workflow_management_api/get_on_behalf_info/${this.customer_application}/`;

      $.ajax({
        method: "GET",
        url: url,
        contentType: "application/json",
        headers: {
          Authorization: `Token ${token}`,
        },
        success: (response) => {
          if (response.status === "success" && response.data) {
            this.on_behalf_info = response.data;
          }
        },
        error: () => {
          this.on_behalf_info = null;
        },
      });
    },

    // asset info
    get_asset_info() {
      const token = localStorage.getItem("token");
      const base_url = process.env.API_BASE_URL;

      const url = `${base_url}/verify_api/customer_management_api/get_asset_application_info/${this.customer_application}/`;

      $.ajax({
        method: "GET",
        url: url,
        contentType: "application/json",
        headers: {
          Authorization: `Token ${token}`,
        },
        success: (response) => {
          if (response.status === "success" && response.asset_data) {
            this.asset_info = response.asset_data;
          }
        },
        error: () => {
          this.asset_info = null;
        },
      });
    },
    loadCustomersRiskData() {
      const TOKEN = localStorage.getItem("token");

      if (!this.workflow_id || !this.customer_application) {
        return;
      }

      this.riskDataMap.risk_loading = true;

      $.ajax({
        url: `${API_BASE_URL}/verify_api/workflow_management_api/customers_scores/`,
        method: "GET",
        contentType: "application/json",
        headers: { Authorization: `Token ${TOKEN}` },
        data: {
          workflow_id: this.workflow_id,
          customer_application: this.customer_application,
        },
        success: (response) => {
          if (
            response.status === "success" &&
            response.data &&
            response.data.length
          ) {
            const latestApp = response.data.reduce((latest, app) => {
              return !latest || app.application_id > latest.application_id
                ? app
                : latest;
            }, null);

            this.riskDataMap = {
              risk_loading: false,
              risk_data: latestApp?.services || [],
              risk_error: latestApp ? null : "No risk data",
            };
          } else {
            this.riskDataMap = {
              risk_loading: false,
              risk_data: [],
              risk_error: "No risk data",
            };
          }
        },
        error: () => {
          this.riskDataMap = {
            risk_loading: false,
            risk_data: [],
            risk_error: "Failed to load risk data",
          };
        },
      });
    },
    handleRiskServiceClick(service) {
      this.$emit("service-clicked", service);
    },
    triggerZohoSend() {
      const isAlreadyExpanded = this.expandedSections && this.expandedSections.zoho;
      
      if (isAlreadyExpanded) {
        if (this.$refs.zohoApplication) {
          this.$refs.zohoApplication.send_all_documents({ amlTargetModule: this.amlTargetModule });
        }
      } else {
        if (this.expandedSections) {
          Object.keys(this.expandedSections).forEach((key) => {
            this.expandedSections[key] = false;
          });
          this.expandedSections.zoho = true;
        }

        setTimeout(() => {
          if (this.$refs.zohoApplication) {
            this.$refs.zohoApplication.send_all_documents({ amlTargetModule: this.amlTargetModule });
          }
        }, 300);
      }
    },
    handleZohoSendClick() {
      if (this.hasAmlZohoReport) {
        this.pendingAmlTargetModule = this.amlTargetModule || "Contacts";
        this.showAmlDestinationModal = true;
        return;
      }

      this.triggerZohoSend();
    },
    closeAmlDestinationModal() {
      this.showAmlDestinationModal = false;
      this.pendingAmlTargetModule = this.amlTargetModule || "Contacts";
    },
    confirmAmlDestinationSelection() {
      this.amlTargetModule = this.pendingAmlTargetModule || "Contacts";
      this.showAmlDestinationModal = false;
      this.triggerZohoSend();
    },
    handleAmlTargetModuleSelected(moduleName) {
      this.amlTargetModule = moduleName || "Contacts";
      this.pendingAmlTargetModule = this.amlTargetModule;
    },
    handleServicesLoaded(services_data) {
      this.services_data = services_data;
    },
    handleSendingStatusChanged(is_sending) {
      this.is_sending_to_zoho = is_sending;
    },
    handleZohoDocumentSent(payload) {
      if (this.credit_bureau_services && Array.isArray(this.credit_bureau_services)) {
        const service = this.credit_bureau_services.find(
          (s) => s.service_name === payload.service_name
        );
        
        if (service) {
          service.zoho_sent_at = payload.sent_at;
        }
      }
    },
    openSeritiPolicy() {
      Object.keys(this.expandedSections).forEach((key) => {
        this.expandedSections[key] = false;
      });
      this.expandedSections.seriti = true;
      if (!this.seritiMounted) {
        this.seritiMounted = true;
      }
    },
    toggleExpanded(section) {
      if (!this.expandedSections) return;

      if (this.expandedSections[section]) {
        this.expandedSections[section] = false;
        return;
      }

      Object.keys(this.expandedSections).forEach((key) => {
        this.expandedSections[key] = false;
      });

      if (section === 'seriti' && !this.seritiMounted) {
        this.seritiMounted = true;
      } else if (section === 'signio' && !this.signioMounted) {
        this.signioMounted = true;
      } else if (section === 'internalForms' && !this.internalFormsMounted) {
        this.internalFormsMounted = true;
      }

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.expandedSections[section] = true;
          });
        });
      });
    },
  },
  watch: {
    workflow_id: {
      immediate: true,
      handler() {
        if (this.workflow_id && this.customer_application) {
          this.get_asset_info();
          this.get_on_behalf_info();
          this.loadCustomersRiskData();
          this.fetchWorkflowStatuses();
          this.fetchCurrentApplicationStatus();
          this.$nextTick(() => {
            this.get_workflow_details();
          });
        }
      },
    },
    customer_application: {
      immediate: true,
      handler() {
        if (this.workflow_id && this.customer_application) {
          this.get_asset_info();
          this.get_on_behalf_info();
          this.loadCustomersRiskData();
          this.fetchWorkflowStatuses();
          this.fetchCurrentApplicationStatus();
          this.$nextTick(() => {
            this.get_workflow_details();
          });
        }
      },
    },
  },
};
</script>

<style scoped>
.customer-initial-icon {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  font-size: 42px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.customer-initial-icon:hover {
  transform: scale(1.05);
}

.icon-line {
  height: 2px;
  background-color: #dee2e6;
  max-width: 450px;
}

.customer-info-group {
  min-width: 250px;
}

.customer-info-row {
  flex-wrap: wrap;
}

.info-value {
  min-height: 1.2rem;
}

.divider-line {
  color: #dee2e6;
  font-size: 1.2rem;
  font-weight: 300;
  padding: 0 0.5rem;
}

.asset-thumbnail-wrapper {
  width: 120px;
  height: 120px;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  background: #f8f9fa;
}

.asset-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.2s ease;
}

.asset-thumbnail:hover {
  transform: scale(1.1);
}

.collapsible-section {
  position: relative;
  transition: all 0.3s ease;
}

.collapsible-header {
  cursor: pointer;
  user-select: none;
}

.collapsible-icon {
  font-size: 0.7rem;
  transition: transform 0.2s ease;
}

.collapsible-content {
  position: relative;
}

.collapsible-content.expanded {
  margin-top: 0.5rem;
}

.accordion-fade-enter-active {
  transition: opacity 0.25s ease-out, transform 0.25s ease-out;
}

.accordion-fade-leave-active {
  transition: opacity 0.15s ease-in, transform 0.15s ease-in;
}

.accordion-fade-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.accordion-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.accordion-fade-enter-to,
.accordion-fade-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

</style>

<!-- Global styles for teleported modal -->
<style>
.customer-profile-modal-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background-color: rgba(0, 0, 0, 0.5) !important;
  z-index: 10150 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  overflow: auto;
}

.customer-profile-modal-dialog {
  position: relative;
  width: 100%;
  max-width: 500px;
  margin: 1.75rem auto;
  pointer-events: none;
}

.customer-profile-modal-content {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  pointer-events: auto;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  isolation: isolate;
}

.customer-profile-modal-header {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1rem;
  border-bottom: 1px solid #dee2e6;
  border-top-left-radius: calc(0.5rem - 1px);
  border-top-right-radius: calc(0.5rem - 1px);
}

.customer-profile-modal-body {
  position: relative;
  flex: 1 1 auto;
  padding: 1rem;
}

.customer-profile-modal-footer {
  display: flex;
  flex-wrap: wrap;
  flex-shrink: 0;
  align-items: center;
  justify-content: flex-end;
  padding: 0.75rem;
  border-top: 1px solid #dee2e6;
  border-bottom-right-radius: calc(0.5rem - 1px);
  border-bottom-left-radius: calc(0.5rem - 1px);
  gap: 0.5rem;
}

/* Modal fade transition */
.modal-fade-enter-active {
  transition: opacity 0.2s ease-out;
}

.modal-fade-leave-active {
  transition: opacity 0.15s ease-in;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .customer-profile-modal-dialog {
  transition: transform 0.2s ease-out, opacity 0.2s ease-out;
}

.modal-fade-leave-active .customer-profile-modal-dialog {
  transition: transform 0.15s ease-in, opacity 0.15s ease-in;
}

.modal-fade-enter-from .customer-profile-modal-dialog {
  transform: scale(0.95) translateY(-10px);
  opacity: 0;
}

.modal-fade-leave-to .customer-profile-modal-dialog {
  transform: scale(0.98);
  opacity: 0;
}

.aml-target-modal-overlay {
  position: fixed !important;
  inset: 0 !important;
  z-index: 10150 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 1.5rem !important;
  background: rgba(15, 23, 42, 0.42) !important;
  backdrop-filter: blur(6px);
}

.aml-target-modal-card {
  width: min(100%, 440px);
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.22);
  overflow: hidden;
}

.aml-target-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.25rem 0.85rem;
}

.aml-target-modal-kicker {
  margin-bottom: 0.3rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #64748b;
}

.aml-target-modal-title {
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 700;
}

.aml-target-modal-body {
  padding: 0 1.25rem 1rem;
}

.aml-target-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 0 1.25rem 1.25rem;
}

.aml-target-modal-fade-enter-active,
.aml-target-modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.aml-target-modal-fade-enter-from,
.aml-target-modal-fade-leave-to {
  opacity: 0;
}

@media (max-width: 575.98px) {
  .aml-target-modal-overlay {
    padding: 1rem !important;
    align-items: flex-end !important;
  }

  .aml-target-modal-card {
    width: 100%;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  .aml-target-modal-footer {
    flex-direction: column-reverse;
  }

  .aml-target-modal-footer .btn {
    width: 100%;
  }
}
</style>
